import React, { useEffect } from 'react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

export const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="landing-page" data-testid="privacy-page">
      <SiteHeader />

      <section className="page-content-section">
        <div className="container page-container-inner">
          <div className="legal-content" data-testid="privacy-content">
              <h1 className="legal-title">BuckTime! Privacy Policy</h1>
              <p className="legal-meta">Effective Date: March 6, 2026 | Last Updated: August 31, 2026</p>

              <h2>DEFINITIONS AND INTERPRETATION</h2>
              <h3>1.1 Definitions</h3>
              <p>In this Privacy Policy:</p>
              <p>&quot;Active Boost&quot; means the optional activity-based feature that utilizes activity-recognition information, including step counts and movement-related metrics, to support rewards, incentives, bonuses, achievements, and related functionality.</p>
              <p>&quot;Active Guard&quot; means the monitoring functionality that assists Parent Account holders in determining whether designated applications have been used during a Focus Window and, where the Daily Balance earning model is in use, in determining how much time designated applications were used across a calendar day, as described in sections 6.6 to 6.11.</p>
              <p>&quot;All-Day Measurement&quot; means the functionality that records the amount of time designated applications were used across a calendar day, including outside a Focus Window. All-Day Measurement forms part of the Daily Balance earning model and operates wherever that model is in use. It is not separately switched on or off by a Parent Account holder.</p>
              <p>&quot;App&quot; means the BuckTime! mobile application, website, software, content, features, support services, notifications, analytics tools, and related technologies.</p>
              <p>&quot;Child Profile&quot; means a profile created and administered by a Parent Account holder on behalf of a child.</p>
              <p>&quot;Company&quot;, &quot;BuckTime!&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot; means 2422019 Ontario Inc. (o/a BuckTime!), together with its affiliates, successors, and assigns.</p>
              <p>&quot;Family Group&quot; means a collection of Parent Accounts, Child Profiles, Linked Devices, settings, rewards, and related information associated with a household.</p>
              <p>&quot;Focus Window&quot; means a period designated by a Parent Account holder during which Active Guard attempts to determine whether designated applications have been accessed.</p>
              <p>&quot;Linked Device&quot; means any smartphone, tablet, wearable device, computer, gaming device, or other supported device associated with a Child Profile or Family Group.</p>
              <p>&quot;Parent Account&quot; means the primary account established by a parent, guardian, or other authorized individual that administers one or more Child Profiles.</p>
              <p>&quot;Parent Account Holder&quot; means the individual who creates, controls, and manages a Parent Account.</p>
              <p>&quot;Personal Information&quot; means information about an identifiable individual and shall be interpreted in accordance with applicable privacy legislation.</p>
              <p>&quot;Privacy Policy&quot; means this Privacy Policy, as amended from time to time.</p>
              <p>&quot;Service Provider&quot; means a third party engaged by the Company to assist in the operation, support, hosting, maintenance, security, analytics, notification delivery, authentication, or administration of the App.</p>
              <p>&quot;Terms of Service&quot; means the BuckTime! Terms of Service, as amended from time to time.</p>
              <p>&quot;Usage Data&quot; means technical, operational, diagnostic, analytical, performance, and activity-related information generated through use of the App.</p>
              <h3>1.2 Interpretation</h3>
              <p>Unless the context otherwise requires:</p>
              <ul>
                <li>(a) singular words include the plural and vice versa;</li>
                <li>(b) references to legislation include amendments, replacements, and successor legislation;</li>
                <li>(c) headings are for convenience only and do not affect interpretation;</li>
                <li>(d) the words &quot;including&quot;, &quot;includes&quot;, and &quot;such as&quot; mean &quot;including without limitation&quot;; and</li>
                <li>(e) references to a child include any Child Profile administered through the App.</li>
              </ul>
              <h2>INTRODUCTION AND SCOPE</h2>
              <h3>2.1 Our Commitment to Privacy</h3>
              <p>BuckTime! is committed to protecting the privacy, security, and confidentiality of information relating to parents, guardians, children, and other users of the App.</p>
              <p>Because the App is designed for family use and involves information relating to children, we recognize that enhanced privacy protections and transparency are essential.</p>
              <p>This Privacy Policy describes how we collect, use, disclose, store, process, retain, transfer, and protect information when users access or use the App.</p>
              <h3>2.2 Scope of this Privacy Policy</h3>
              <p>This Privacy Policy applies to:</p>
              <ul>
                <li>Parent Accounts;</li>
                <li>Child Profiles;</li>
                <li>Family Groups;</li>
                <li>visitors to our website;</li>
                <li>customer support interactions;</li>
                <li>users of notifications and communications services;</li>
                <li>subscription holders; and</li>
                <li>users of any feature or functionality made available through the App.</li>
              </ul>
              <p>This Privacy Policy applies whether information is collected through:</p>
              <ul>
                <li>the mobile application;</li>
                <li>websites operated by the Company;</li>
                <li>customer-support communications;</li>
                <li>account-registration processes; or</li>
                <li>other interactions with the Company.</li>
              </ul>
              <h3>2.3 Relationship with the Terms of Service</h3>
              <p>This Privacy Policy should be read together with the Terms of Service.</p>
              <p>In the event of any inconsistency between this Privacy Policy and the Terms of Service regarding privacy matters, this Privacy Policy shall govern to the extent of the inconsistency.</p>
              <h3>2.4 Geographic Scope</h3>
              <p>The App is intended primarily for users located in Canada, although users may access the App from other jurisdictions.</p>
              <p>By using the App, users acknowledge that information may be processed in jurisdictions other than their place of residence as described in this Privacy Policy.</p>
              <h2>CHILDREN’S PRIVACY AND PARENTAL CONSENT</h2>
              <h3>3.1 Family-Oriented Service</h3>
              <p>The App is designed as a family-management platform intended to assist parents and guardians in encouraging healthy technology habits, administering rewards programs, and monitoring designated applications during Focus Windows and, where the Daily Balance earning model is in use, across the calendar day.</p>
              <p>The App is not intended for independent registration by children.</p>
              <h3>3.2 Parent-Controlled Registration</h3>
              <p>Children may not independently create accounts.</p>
              <p>All Child Profiles must be created by a Parent Account holder.</p>
              <p>The Company does not knowingly permit children to independently establish accounts or directly register for the App.</p>
              <h3>3.3 Parental Authorization</h3>
              <p>By creating a Child Profile, the Parent Account holder represents and warrants that:</p>
              <ul>
                <li>(a) they are the child’s parent, legal guardian, or other legally authorized decision-maker;</li>
                <li>(b) they possess authority to provide privacy consents on behalf of the child;</li>
                <li>(c) they possess authority to authorize use of Active Guard and related functionality;</li>
                <li>(d) they have reviewed this Privacy Policy; and</li>
                <li>(e) they consent to the collection, use, disclosure, storage, processing, retention, and transfer of information relating to the child as described herein.</li>
              </ul>
              <h3>3.4 Child Information Submitted by Parents</h3>
              <p>Information associated with a Child Profile is provided by the Parent Account holder or generated through use of the App.</p>
              <p>The Company does not knowingly solicit personal information directly from children without parental involvement.</p>
              <h3>3.5 Withdrawal of Consent</h3>
              <p>A Parent Account holder may withdraw consent for a Child Profile at any time.</p>
              <p>Withdrawal of consent may result in:</p>
              <ul>
                <li>suspension of the Child Profile;</li>
                <li>deletion of Child Profile information;</li>
                <li>loss of access to certain functionality; and</li>
                <li>closure of the Child Profile.</li>
              </ul>
              <p>Certain information may be retained where required by law or reasonably necessary for security, fraud prevention, legal compliance, dispute resolution, backup integrity, or enforcement purposes.</p>
              <h3>3.6 Unauthorized Child Registration</h3>
              <p>If we learn that a child has attempted to independently register for the App or has provided information without appropriate parental authorization, we may:</p>
              <ul>
                <li>suspend access;</li>
                <li>restrict functionality;</li>
                <li>seek parental verification;</li>
                <li>delete information; or</li>
                <li>terminate the applicable account.</li>
              </ul>
              <h2>INFORMATION WE COLLECT FROM PARENTS</h2>
              <h3>4.1 Account Registration Information</h3>
              <p>When a Parent Account is created, we may collect:</p>
              <ul>
                <li>name;</li>
                <li>email address;</li>
                <li>authentication information;</li>
                <li>Google account identifier;</li>
                <li>email-verification status;</li>
                <li>account-creation date;</li>
                <li>account-modification date;</li>
                <li>family identifier; and</li>
                <li>timezone information.</li>
              </ul>
              <h3>4.2 Google Sign-In Information</h3>
              <p>Where a Parent Account holder elects to use Google Sign-In, we may receive information from Google including:</p>
              <ul>
                <li>name;</li>
                <li>email address;</li>
                <li>Google account identifier; and</li>
                <li>profile photo URL.</li>
              </ul>
              <p>The profile photograph itself is not uploaded to, hosted by, or stored by BuckTime!. We retain only the profile image reference supplied through Google’s authentication service.</p>
              <h3>4.3 Account Credentials</h3>
              <p>For accounts created using email and password authentication:</p>
              <ul>
                <li>passwords are stored in encrypted and protected form;</li>
                <li>raw passwords are never stored by the Company; and</li>
                <li>authentication credentials are processed solely for account-security purposes.</li>
              </ul>
              <h3>4.4 Support Communications</h3>
              <p>If a Parent Account holder contacts us for support, we may collect:</p>
              <ul>
                <li>contact information;</li>
                <li>account information;</li>
                <li>correspondence;</li>
                <li>support requests;</li>
                <li>troubleshooting information; and</li>
                <li>information voluntarily provided by the user.</li>
              </ul>
              <h3>4.5 Subscription and Billing Information</h3>
              <p>If paid subscriptions or premium services are introduced, we may collect subscription-related information including:</p>
              <ul>
                <li>subscription status;</li>
                <li>billing status;</li>
                <li>transaction identifiers; and</li>
                <li>related account information.</li>
              </ul>
              <p>Payment-card information is generally processed by third-party payment providers and not stored directly by the Company.</p>
              <h2>INFORMATION WE COLLECT ABOUT CHILDREN</h2>
              <h3>5.1 Child Profile Information</h3>
              <p>When a Parent Account holder creates a Child Profile, we may collect:</p>
              <ul>
                <li>child name;</li>
                <li>nickname or preferred name;</li>
                <li>avatar selection;</li>
                <li>profile settings;</li>
                <li>reward settings;</li>
                <li>earning settings;</li>
                <li>allowance information;</li>
                <li>Ledger information;</li>
                <li>Family Group information; and</li>
                <li>related account-management information.</li>
              </ul>
              <h3>5.2 Authentication Information</h3>
              <p>Child Profiles may utilize PIN-based authentication.</p>
              <p>PIN credentials are stored using security measures designed to protect against unauthorized access, including cryptographic hashing and related security controls.</p>
              <h3>5.3 Optional Child Email Addresses</h3>
              <p>A Parent Account holder may optionally provide a child’s email address.</p>
              <p>Such information is collected only where voluntarily entered by the Parent Account holder and may be used in connection with future reward or payment functionality.</p>
              <h3>5.4 Activity and Reward Information</h3>
              <p>We may collect information relating to:</p>
              <ul>
                <li>earnings;</li>
                <li>balances;</li>
                <li>rewards;</li>
                <li>allowances;</li>
                <li>achievements;</li>
                <li>streaks;</li>
                <li>bonus calculations;</li>
                <li>activity metrics; and</li>
                <li>participation in App features.</li>
              </ul>
              <h3>5.5 Device Information Associated with Child Profiles</h3>
              <p>We may collect device-related information associated with a Child Profile, including:</p>
              <ul>
                <li>device manufacturer;</li>
                <li>device model;</li>
                <li>operating-system information;</li>
                <li>device capabilities;</li>
                <li>timezone information;</li>
                <li>language settings;</li>
                <li>notification identifiers;</li>
                <li>permission status information; and</li>
                <li>related technical information.</li>
              </ul>
              <h3>5.6 Information We Do Not Intentionally Collect from Children</h3>
              <p>Unless expressly disclosed elsewhere in this Privacy Policy, we do not intentionally collect:</p>
              <ul>
                <li>precise GPS location information;</li>
                <li>contact lists;</li>
                <li>browsing history;</li>
                <li>text-message content;</li>
                <li>email content;</li>
                <li>photographs stored on the device;</li>
                <li>video files stored on the device;</li>
                <li>microphone recordings;</li>
                <li>financial account information; or</li>
                <li>government-issued identification numbers.</li>
              </ul>
              <h2>ACTIVE GUARD MONITORING INFORMATION</h2>
              <h3>6.1 Purpose of Active Guard</h3>
              <p>Active Guard is designed to assist Parent Account holders in encouraging healthy technology habits by determining whether designated applications have been used during a Focus Window and, where the Daily Balance earning model is in use, how much time designated applications were used across a calendar day.</p>
              <p>Active Guard is intended solely as a family-management and parental-assistance tool.</p>
              <h3>6.2 Information Collected Through Active Guard</h3>
              <p>When Active Guard is enabled, the App may collect, generate, process, or store information including:</p>
              <ul>
                <li>monitored application identifiers;</li>
                <li>application package names;</li>
                <li>application category information;</li>
                <li>application usage events;</li>
                <li>foreground application activity;</li>
                <li>Focus Window participation information;</li>
                <li>monitoring status information;</li>
                <li>monitoring configuration settings;</li>
                <li>permission status information;</li>
                <li>device compatibility information; and</li>
                <li>related technical and diagnostic information.</li>
              </ul>
              <h3>6.3 Information Not Collected Through Active Guard</h3>
              <p>Active Guard is designed to identify application activity and is not designed to access or collect:</p>
              <ul>
                <li>text-message contents;</li>
                <li>email contents;</li>
                <li>instant-message contents;</li>
                <li>chat contents;</li>
                <li>photographs;</li>
                <li>videos;</li>
                <li>documents;</li>
                <li>files stored on a device;</li>
                <li>contact lists;</li>
                <li>passwords;</li>
                <li>keystrokes;</li>
                <li>screen recordings;</li>
                <li>screenshots;</li>
                <li>microphone recordings; or</li>
                <li>browsing-history contents.</li>
              </ul>
              <p>The Company does not intentionally review, access, or store content viewed by users within third-party applications.</p>
              <h3>6.4 Monitoring Limitations</h3>
              <p>Active Guard relies upon operating-system permissions, accessibility services, usage-access permissions, device settings, and platform functionality.</p>
              <p>Accordingly, monitoring information may be affected by:</p>
              <ul>
                <li>operating-system restrictions;</li>
                <li>device settings;</li>
                <li>user actions;</li>
                <li>permission changes;</li>
                <li>software updates;</li>
                <li>hardware limitations;</li>
                <li>connectivity interruptions;</li>
                <li>manufacturer restrictions; and</li>
                <li>third-party platform changes.</li>
              </ul>
              <p>The Company does not guarantee that monitoring information will be complete, uninterrupted, or entirely accurate.</p>
              <h3>6.5 Monitoring Information Retention</h3>
              <p>Monitoring information may be retained for purposes including:</p>
              <ul>
                <li>displaying historical information;</li>
                <li>generating reports;</li>
                <li>producing analytics;</li>
                <li>calculating rewards;</li>
                <li>troubleshooting;</li>
                <li>fraud prevention;</li>
                <li>security monitoring; and</li>
                <li>improving App functionality.</li>
              </ul>
              <p>Retention periods may vary depending upon the nature of the information and operational requirements.</p>
              <h3>6.6 All-Day Measurement</h3>
              <p>Where the Daily Balance earning model is in use, the App records the amount of time designated applications were used across a calendar day, including outside a Focus Window. This measurement is integral to that model: the day’s reward is reduced by use of designated applications whenever it occurs, and measurement confined to Focus Windows would not record it.</p>
              <p>This functionality exists so that a Child Profile may earn rewards for reduced overall use of designated applications, rather than only for use during a scheduled period.</p>
              <h3>6.7 Information Recorded Through All-Day Measurement</h3>
              <p>All-Day Measurement records totals. It does not record a history of activity. The information recorded consists of:</p>
              <ul>
                <li>the total number of seconds designated applications were used during the calendar day;</li>
                <li>on Android devices, that total apportioned across the twenty-four hours of the day;</li>
                <li>on Apple devices, the cumulative usage totals reported by Apple’s Screen Time functionality when they pass thresholds set by the App, and the times at which those thresholds were passed;</li>
                <li>the device’s timezone offset and the time at which the total was calculated, so that a day is measured against the Child Profile’s local calendar day; and</li>
                <li>technical information identifying which component of the App delivered the report.</li>
              </ul>
              <h3>6.8 Information Not Recorded Through All-Day Measurement</h3>
              <p>The information recorded through All-Day Measurement does not identify which designated application was used, in what order applications were used, or what was done within any application. Application names and application identifiers do not form part of this record.</p>
              <h3>6.9 Applications Included in All-Day Measurement</h3>
              <p>Only applications designated by the Parent Account holder are counted. Time spent in any other application is not counted. The BuckTime! application itself is excluded, so that use of the App is never counted against a Child Profile.</p>
              <p>Where the App is unable to determine which applications have been designated, no information is recorded for that period. The App does not record a day of zero use in these circumstances.</p>
              <h3>6.10 How All-Day Measurement Is Performed</h3>
              <p>On Android devices, All-Day Measurement uses the operating system’s usage-access statistics, which the Parent Account holder authorizes during setup.</p>
              <p>On Apple devices, All-Day Measurement uses Apple’s Screen Time functionality, which reports to the App only that cumulative use of designated applications has passed a threshold. Apple does not disclose the underlying detail to the Company.</p>
              <p>Neither method accesses or records the contents of any application.</p>
              <h3>6.11 Where All-Day Measurement Does Not Apply</h3>
              <p>Where the Daily Balance earning model is not in use, no information described in sections 6.6 to 6.10 is recorded outside a Focus Window.</p>
              <h2>ACTIVE BOOST ACTIVITY INFORMATION</h2>
              <h3>7.1 Purpose of Active Boost</h3>
              <p>Active Boost is an optional feature intended to encourage physical activity through activity-based rewards, achievements, incentives, and bonus calculations.</p>
              <h3>7.2 Activity Information Collected</h3>
              <p>Where Active Boost is enabled, the App may collect or receive activity-related information including:</p>
              <ul>
                <li>step counts;</li>
                <li>movement metrics;</li>
                <li>activity measurements;</li>
                <li>exercise-related information;</li>
                <li>activity goals;</li>
                <li>achievement information;</li>
                <li>reward calculations; and</li>
                <li>related performance information.</li>
              </ul>
              <h3>7.3 Sources of Activity Information</h3>
              <p>Activity information may be obtained through:</p>
              <ul>
                <li>operating-system services;</li>
                <li>activity-recognition services;</li>
                <li>supported device sensors; and</li>
                <li>other authorized system functionality.</li>
              </ul>
              <h3>7.4 Health and Medical Disclaimer</h3>
              <p>Active Boost is intended solely for motivational and reward purposes.</p>
              <p>The App is not:</p>
              <ul>
                <li>a medical device;</li>
                <li>a healthcare service;</li>
                <li>a fitness-monitoring service;</li>
                <li>a diagnostic tool; or</li>
                <li>a substitute for professional medical advice.</li>
              </ul>
              <p>Users should not rely upon Active Boost information for medical, health, or fitness decisions.</p>
              <h3>7.5 Accuracy of Activity Information</h3>
              <p>Activity information is generated through third-party operating systems and device sensors.</p>
              <p>Accordingly, activity information may be incomplete, delayed, inaccurate, unavailable, or affected by technical limitations.</p>
              <p>The Company does not guarantee the accuracy of activity-related information.</p>
              <h2>DEVICE INFORMATION AND PERMISSIONS</h2>
              <h3>8.1 Device Information</h3>
              <p>The App may collect technical information relating to devices connected to the App, including:</p>
              <ul>
                <li>device manufacturer;</li>
                <li>device model;</li>
                <li>operating-system version;</li>
                <li>operating-system build information;</li>
                <li>timezone information;</li>
                <li>language settings;</li>
                <li>notification identifiers;</li>
                <li>permission status information;</li>
                <li>technical diagnostics; and</li>
                <li>security-related information.</li>
              </ul>
              <h3>8.2 Android Permissions</h3>
              <p>Depending upon functionality enabled by the user, the App may request certain device permissions.</p>
              <p>These permissions may include:</p>
              <ul>
                <li>Usage Access permissions;</li>
                <li>Accessibility Service permissions;</li>
                <li>Notification permissions;</li>
                <li>Camera permissions;</li>
                <li>Activity Recognition permissions;</li>
                <li>Overlay permissions;</li>
                <li>Background-service permissions;</li>
                <li>Wake-lock permissions; and</li>
                <li>related operating-system permissions required for functionality.</li>
              </ul>
              <h3>8.3 Purpose of Permissions</h3>
              <p>Permissions are requested solely for purposes including:</p>
              <ul>
                <li>operation of Active Guard;</li>
                <li>operation of Active Boost;</li>
                <li>Focus Window functionality;</li>
                <li>notification delivery;</li>
                <li>QR-code pairing;</li>
                <li>monitoring functionality;</li>
                <li>activity-based rewards;</li>
                <li>device compatibility; and</li>
                <li>related App functionality.</li>
              </ul>
              <h3>8.4 Permissions Not Requested</h3>
              <p>As of the effective date of this Privacy Policy, the App does not intentionally request permissions for:</p>
              <ul>
                <li>precise GPS location tracking;</li>
                <li>contact-list access;</li>
                <li>text-message access;</li>
                <li>call-history access; or</li>
                <li>microphone recording for monitoring purposes.</li>
              </ul>
              <p>Future versions of the App may introduce additional permissions, which will be disclosed through updated privacy notices and consent mechanisms where required by law.</p>
              <h3>8.5 Device Diagnostics</h3>
              <p>The Company may collect diagnostic information to:</p>
              <ul>
                <li>identify software defects;</li>
                <li>investigate crashes;</li>
                <li>improve performance;</li>
                <li>maintain compatibility;</li>
                <li>improve security; and</li>
                <li>support users.</li>
              </ul>
              <h2>INFORMATION WE DO NOT COLLECT</h2>
              <h3>9.1 General Principle</h3>
              <p>The Company seeks to collect only the information reasonably necessary to operate, maintain, improve, and support the App.</p>
              <h3>9.2 Information Not Intentionally Collected</h3>
              <p>Unless expressly disclosed elsewhere in this Privacy Policy, the Company does not intentionally collect:</p>
              <ul>
                <li>precise GPS location information;</li>
                <li>contact lists;</li>
                <li>text-message contents;</li>
                <li>email contents;</li>
                <li>chat contents;</li>
                <li>photographs stored on a device;</li>
                <li>videos stored on a device;</li>
                <li>financial account credentials;</li>
                <li>banking passwords;</li>
                <li>government-issued identification numbers;</li>
                <li>passport information;</li>
                <li>driver’s licence information;</li>
                <li>biometric identifiers;</li>
                <li>voice recordings;</li>
                <li>microphone recordings;</li>
                <li>browsing-history contents;</li>
                <li>health records;</li>
                <li>medical records; or</li>
                <li>information unrelated to operation of the App.</li>
              </ul>
              <h3>9.3 Child Information Minimization</h3>
              <p>The Company is committed to data-minimization principles and seeks to limit the collection of information relating to children to information reasonably necessary to provide the App.</p>
              <h3>9.4 No Sale of Personal Information</h3>
              <p>The Company does not sell personal information.</p>
              <p>The Company does not knowingly sell information associated with Child Profiles.</p>
              <h3>9.5 No Behavioural Advertising Directed at Children</h3>
              <p>The Company does not knowingly use Child Profile information for:</p>
              <ul>
                <li>behavioural advertising;</li>
                <li>interest-based advertising;</li>
                <li>cross-context advertising; or</li>
                <li>targeted advertising directed at children.</li>
              </ul>
              <h2>HOW WE USE INFORMATION</h2>
              <h3>10.1 Service Delivery</h3>
              <p>The Company uses information to:</p>
              <ul>
                <li>create and administer accounts;</li>
                <li>create Child Profiles;</li>
                <li>operate Family Groups;</li>
                <li>authenticate users;</li>
                <li>provide App functionality;</li>
                <li>operate Active Guard;</li>
                <li>operate Active Boost;</li>
                <li>provide rewards functionality; and</li>
                <li>deliver notifications.</li>
              </ul>
              <h3>10.2 Analytics and Reporting</h3>
              <p>Information may be used to:</p>
              <ul>
                <li>generate analytics;</li>
                <li>produce dashboards;</li>
                <li>calculate rewards;</li>
                <li>calculate earnings;</li>
                <li>measure engagement;</li>
                <li>generate reports;</li>
                <li>improve App functionality; and</li>
                <li>develop future features.</li>
              </ul>
              <h3>10.3 Security and Fraud Prevention</h3>
              <p>Information may be used to:</p>
              <ul>
                <li>verify accounts;</li>
                <li>detect unauthorized access;</li>
                <li>investigate misuse;</li>
                <li>prevent fraud;</li>
                <li>maintain security;</li>
                <li>identify software vulnerabilities; and</li>
                <li>enforce the Terms of Service.</li>
              </ul>
              <h3>10.4 Customer Support</h3>
              <p>Information may be used to:</p>
              <ul>
                <li>respond to inquiries;</li>
                <li>provide support;</li>
                <li>troubleshoot issues;</li>
                <li>investigate complaints;</li>
                <li>resolve disputes; and</li>
                <li>communicate with users.</li>
              </ul>
              <h3>10.5 Legal and Regulatory Compliance</h3>
              <p>Information may be used to:</p>
              <ul>
                <li>comply with legal obligations;</li>
                <li>respond to lawful requests;</li>
                <li>cooperate with regulators;</li>
                <li>enforce legal rights;</li>
                <li>investigate violations of law; and</li>
                <li>protect users, children, and the public.</li>
              </ul>
              <h3>10.6 Product Improvement</h3>
              <p>Information may be used to:</p>
              <ul>
                <li>improve functionality;</li>
                <li>enhance user experience;</li>
                <li>evaluate performance;</li>
                <li>improve reliability;</li>
                <li>improve security;</li>
                <li>develop new features; and</li>
                <li>conduct internal research and development.</li>
              </ul>
              <h3>10.7 Aggregated and De-Identified Information</h3>
              <p>The Company may create aggregated, anonymized, statistical, or de-identified information derived from App usage.</p>
              <p>Such information may be used for:</p>
              <ul>
                <li>analytics;</li>
                <li>benchmarking;</li>
                <li>research;</li>
                <li>product development;</li>
                <li>operational planning;</li>
                <li>business analysis; and</li>
                <li>other lawful business purposes.</li>
              </ul>
              <p>Provided such information does not reasonably identify an individual, it may continue to be used following account deletion.</p>
              <h3>10.8 Future Features</h3>
              <p>The Company may use information to provide future functionality including:</p>
              <ul>
                <li>subscriptions;</li>
                <li>premium services;</li>
                <li>enhanced analytics;</li>
                <li>additional rewards systems;</li>
                <li>payment integrations;</li>
                <li>artificial-intelligence features; and</li>
                <li>other services introduced in future versions of the App.</li>
              </ul>
              <p>Where required by law, additional notice or consent will be obtained before implementing materially different uses of information.</p>
              <h2>DISCLOSURE OF INFORMATION</h2>
              <h3>11.1 General Principle</h3>
              <p>The Company does not sell personal information.</p>
              <p>The Company does not knowingly sell information associated with Child Profiles.</p>
              <p>The Company does not disclose personal information except as described in this Privacy Policy, as authorized by users, or as otherwise permitted or required by law.</p>
              <h3>11.2 Disclosure to Service Providers</h3>
              <p>The Company may disclose information to Service Providers that assist with:</p>
              <ul>
                <li>cloud hosting;</li>
                <li>database hosting;</li>
                <li>authentication services;</li>
                <li>push notifications;</li>
                <li>analytics;</li>
                <li>customer support;</li>
                <li>software maintenance;</li>
                <li>cybersecurity services;</li>
                <li>infrastructure management;</li>
                <li>subscription management;</li>
                <li>payment processing; and</li>
                <li>other operational functions.</li>
              </ul>
              <p>Service Providers receive only the information reasonably necessary to perform their services.</p>
              <h3>11.3 Disclosure Within Family Groups</h3>
              <p>Information associated with a Child Profile may be made available to Parent Account holders associated with the applicable Family Group.</p>
              <p>Such information may include:</p>
              <ul>
                <li>rewards;</li>
                <li>balances;</li>
                <li>activity metrics;</li>
                <li>monitoring information;</li>
                <li>achievements;</li>
                <li>streaks;</li>
                <li>allowance information; and</li>
                <li>related Family Group information.</li>
              </ul>
              <h3>11.4 Legal Compliance</h3>
              <p>The Company may disclose information where reasonably necessary to:</p>
              <ul>
                <li>comply with applicable law;</li>
                <li>comply with court orders;</li>
                <li>respond to subpoenas;</li>
                <li>respond to warrants;</li>
                <li>respond to regulatory requests;</li>
                <li>respond to law-enforcement requests;</li>
                <li>enforce legal rights;</li>
                <li>investigate unlawful conduct; or</li>
                <li>protect users, children, or the public.</li>
              </ul>
              <h3>11.5 Business Transactions</h3>
              <p>Information may be disclosed in connection with:</p>
              <ul>
                <li>mergers;</li>
                <li>acquisitions;</li>
                <li>financings;</li>
                <li>reorganizations;</li>
                <li>asset sales;</li>
                <li>investments;</li>
                <li>restructurings; or</li>
                <li>similar corporate transactions.</li>
              </ul>
              <p>Where appropriate, the Company may require recipients to maintain confidentiality protections consistent with this Privacy Policy.</p>
              <h3>11.6 Protection of Rights</h3>
              <p>The Company may disclose information where reasonably necessary to:</p>
              <ul>
                <li>protect Company rights;</li>
                <li>protect intellectual property;</li>
                <li>investigate fraud;</li>
                <li>investigate misuse;</li>
                <li>maintain security;</li>
                <li>prevent harm; or</li>
                <li>enforce contractual rights.</li>
              </ul>
              <h3>11.7 Aggregated and De-Identified Information</h3>
              <p>The Company may disclose aggregated, anonymized, statistical, or de-identified information that does not reasonably identify an individual.</p>
              <p>Such information may be used for:</p>
              <ul>
                <li>research;</li>
                <li>benchmarking;</li>
                <li>analytics;</li>
                <li>reporting;</li>
                <li>product development;</li>
                <li>security analysis; and</li>
                <li>business planning.</li>
              </ul>
              <h3>11.8 No Sale of Child Information</h3>
              <p>The Company does not knowingly:</p>
              <ul>
                <li>sell Child Profile information;</li>
                <li>disclose Child Profile information for behavioural advertising;</li>
                <li>disclose Child Profile information for interest-based advertising; or</li>
                <li>monetize Child Profile information through third-party advertising networks.</li>
              </ul>
              <h2>SERVICE PROVIDERS AND INFRASTRUCTURE PROVIDERS</h2>
              <h3>12.1 Use of Service Providers</h3>
              <p>The Company utilizes third-party Service Providers to assist in operation of the App.</p>
              <p>These Service Providers may process information on behalf of the Company.</p>
              <h3>12.2 Categories of Service Providers</h3>
              <p>Service Providers may include:</p>
              <ul>
                <li>authentication providers;</li>
                <li>cloud-hosting providers;</li>
                <li>database providers;</li>
                <li>notification providers;</li>
                <li>analytics providers;</li>
                <li>infrastructure providers;</li>
                <li>cybersecurity providers;</li>
                <li>support providers;</li>
                <li>payment providers; and</li>
                <li>other operational vendors.</li>
              </ul>
              <h3>12.3 Access to Information</h3>
              <p>Service Providers may receive access to information only where reasonably necessary to provide services to the Company.</p>
              <p>The Company seeks to limit access to information based upon operational need.</p>
              <h3>12.4 Contractual Safeguards</h3>
              <p>Where appropriate, the Company may require Service Providers to:</p>
              <ul>
                <li>protect information;</li>
                <li>maintain confidentiality;</li>
                <li>implement security safeguards;</li>
                <li>restrict unauthorized use;</li>
                <li>comply with applicable privacy requirements; and</li>
                <li>process information solely for authorized purposes.</li>
              </ul>
              <h3>12.5 Third-Party Policies</h3>
              <p>Service Providers operate under their own privacy practices and contractual obligations.</p>
              <p>While the Company seeks to work with reputable providers, the Company does not control all aspects of a Service Provider’s operations.</p>
              <h3>12.6 Changes to Providers</h3>
              <p>The Company may add, remove, replace, or modify Service Providers at any time.</p>
              <p>Updated information regarding material Service Providers may be reflected in future updates to this Privacy Policy.</p>
              <h2>GOOGLE SIGN-IN AND AUTHENTICATION SERVICES</h2>
              <h3>13.1 Authentication Providers</h3>
              <p>The App may permit authentication through Google Sign-In and other authentication services introduced in future versions of the App.</p>
              <h3>13.2 Information Received Through Google Sign-In</h3>
              <p>When a user elects to authenticate through Google Sign-In, the Company may receive:</p>
              <ul>
                <li>name;</li>
                <li>email address;</li>
                <li>Google account identifier; and</li>
                <li>profile image reference information.</li>
              </ul>
              <p>The specific information received depends upon permissions granted by the user and Google’s authentication services.</p>
              <h3>13.3 Purpose of Authentication Information</h3>
              <p>Authentication information may be used to:</p>
              <ul>
                <li>create accounts;</li>
                <li>authenticate users;</li>
                <li>maintain account security;</li>
                <li>facilitate account recovery;</li>
                <li>reduce fraud;</li>
                <li>provide support; and</li>
                <li>operate the App.</li>
              </ul>
              <h3>13.4 Google’s Independent Practices</h3>
              <p>Google processes information in accordance with its own privacy policies, terms, and practices.</p>
              <p>Users should review Google’s privacy documentation for additional information regarding Google’s handling of information.</p>
              <h3>13.5 Future Authentication Providers</h3>
              <p>The Company may introduce additional authentication providers in the future.</p>
              <p>Additional providers may be subject to separate privacy practices and user disclosures.</p>
              <h2>PUSH NOTIFICATIONS, MESSAGING, AND COMMUNICATIONS</h2>
              <h3>14.1 Push Notifications</h3>
              <p>The App may utilize push-notification services to deliver information to users.</p>
              <p>Push notifications may be delivered through:</p>
              <ul>
                <li>Firebase Cloud Messaging;</li>
                <li>Expo Push Services;</li>
                <li>Apple Push Notification Services; or</li>
                <li>other notification services.</li>
              </ul>
              <h3>14.2 Notification Information</h3>
              <p>To facilitate delivery of notifications, the Company may process:</p>
              <ul>
                <li>push-notification identifiers;</li>
                <li>device tokens;</li>
                <li>notification preferences;</li>
                <li>Family Group information;</li>
                <li>account identifiers; and</li>
                <li>related technical information.</li>
              </ul>
              <h3>14.3 Types of Notifications</h3>
              <p>Notifications may include:</p>
              <ul>
                <li>Focus Window notifications;</li>
                <li>reward notifications;</li>
                <li>earnings notifications;</li>
                <li>allowance notifications;</li>
                <li>activity notifications;</li>
                <li>account-security notifications;</li>
                <li>support communications;</li>
                <li>service announcements;</li>
                <li>administrative communications; and</li>
                <li>legal notices.</li>
              </ul>
              <h3>14.4 User Controls</h3>
              <p>Users may disable or modify certain notification preferences through:</p>
              <ul>
                <li>App settings;</li>
                <li>device settings; or</li>
                <li>operating-system controls.</li>
              </ul>
              <p>Certain administrative, legal, security, and service-related communications may continue notwithstanding notification preferences where permitted by law.</p>
              <h3>14.5 No Emergency Service</h3>
              <p>Notifications are not emergency communications.</p>
              <p>The Company does not guarantee:</p>
              <ul>
                <li>delivery;</li>
                <li>timing;</li>
                <li>accuracy; or</li>
                <li>receipt</li>
              </ul>
              <p>of notifications.</p>
              <p>Users should not rely upon notifications for emergency, medical, safety, or time-sensitive purposes.</p>
              <h2>HOSTING, DATABASES, INTERNATIONAL TRANSFERS, AND DATA RESIDENCY</h2>
              <h3>15.1 Hosting Infrastructure</h3>
              <p>The App utilizes third-party hosting, cloud, infrastructure, and database providers to operate the App.</p>
              <p>Information may be stored, processed, transmitted, backed up, or accessed through such providers.</p>
              <h3>15.2 Database Services</h3>
              <p>The Company currently utilizes MongoDB Atlas and related database infrastructure to support operation of the App.</p>
              <p>Database providers may process information on behalf of the Company in accordance with applicable contractual arrangements.</p>
              <h3>15.3 Application Infrastructure</h3>
              <p>The Company currently utilizes Fly.io and related infrastructure providers to host and operate portions of the App.</p>
              <p>Infrastructure providers may process technical and operational information necessary to provide hosting services.</p>
              <h3>15.4 Data Residency</h3>
              <p>The Company’s primary database environment is currently located in Canada.</p>
              <p>However, not all Service Providers necessarily store or process information exclusively within Canada.</p>
              <h3>15.5 International Transfers</h3>
              <p>Information may be transferred to, stored in, processed in, or accessed from jurisdictions outside:</p>
              <ul>
                <li>Canada;</li>
                <li>a user’s province or territory; or</li>
                <li>a user’s country of residence.</li>
              </ul>
              <p>Such jurisdictions may have privacy laws that differ from those applicable where the user resides.</p>
              <h3>15.6 User Consent to Transfers</h3>
              <p>By using the App, users acknowledge and consent to the transfer, storage, processing, and access of information in jurisdictions outside their place of residence as described in this Privacy Policy.</p>
              <h3>15.7 Legal Access in Foreign Jurisdictions</h3>
              <p>Information processed in foreign jurisdictions may be subject to:</p>
              <ul>
                <li>court orders;</li>
                <li>subpoenas;</li>
                <li>warrants;</li>
                <li>governmental requests;</li>
                <li>regulatory requests; and</li>
                <li>legal processes</li>
              </ul>
              <p>issued under the laws of those jurisdictions.</p>
              <h3>15.8 Transfer Safeguards</h3>
              <p>Where reasonably appropriate, the Company seeks to implement contractual, organizational, and technical safeguards designed to protect information during cross-border processing.</p>
              <h3>15.9 Current Service Providers</h3>
              <p>Current Service Providers and Infrastructure Providers utilized in connection with operation of the App are identified in Schedule &quot;A&quot; to this Privacy Policy, as amended from time to time.</p>
              <p>Schedule &quot;A&quot; is incorporated into and forms part of this Privacy Policy.</p>
              <p>The Company may update Schedule &quot;A&quot; from time to time to reflect changes in Service Providers, hosting arrangements, infrastructure providers, notification providers, authentication providers, payment providers, analytics providers, security providers, and other operational vendors.</p>
              <p>Updates to Schedule &quot;A&quot; do not necessarily constitute a material amendment to this Privacy Policy provided that the Company’s information-handling practices remain substantially consistent with the disclosures contained herein.</p>
              <h3>15.10 Future Infrastructure Changes</h3>
              <p>The Company may modify its hosting, cloud, database, storage, backup, infrastructure, and processing providers from time to time.</p>
              <p>Such changes may result in information being processed in different jurisdictions.</p>
              <p>Material changes may be reflected through updates to this Privacy Policy.</p>
              <h2>DATA RETENTION</h2>
              <h3>16.1 General Retention Principles</h3>
              <p>The Company retains information only for as long as reasonably necessary to:</p>
              <ul>
                <li>provide the App;</li>
                <li>administer Parent Accounts and Child Profiles;</li>
                <li>operate Active Guard and Active Boost;</li>
                <li>maintain Family Groups;</li>
                <li>provide customer support;</li>
                <li>comply with legal obligations;</li>
                <li>maintain security;</li>
                <li>prevent fraud;</li>
                <li>resolve disputes;</li>
                <li>enforce agreements; and</li>
                <li>support legitimate business operations.</li>
              </ul>
              <p>Retention periods vary depending on the nature of the information involved and the purposes for which it was collected.</p>
              <h3>16.2 Account Information</h3>
              <p>Information associated with Parent Accounts and Child Profiles may be retained for the duration of account activity and for a reasonable period thereafter.</p>
              <p>Retention following account closure may occur for purposes including:</p>
              <ul>
                <li>legal compliance;</li>
                <li>security;</li>
                <li>fraud prevention;</li>
                <li>dispute resolution;</li>
                <li>backup integrity;</li>
                <li>recordkeeping; and</li>
                <li>enforcement of contractual rights.</li>
              </ul>
              <h3>16.3 Monitoring Information</h3>
              <p>Information generated through Active Guard may be retained for purposes including:</p>
              <ul>
                <li>historical reporting;</li>
                <li>analytics;</li>
                <li>troubleshooting;</li>
                <li>reward calculations;</li>
                <li>security monitoring; and</li>
                <li>system improvement.</li>
              </ul>
              <p>The Company may periodically delete, archive, anonymize, or aggregate historical monitoring information.</p>
              <h3>16.4 Website Access Requests</h3>
              <p>Where a child asks a Parent Account holder to approve a website, the record consists of the website domain, the date of the request, the Parent Account holder’s decision, and any short explanation the child chose to write.</p>
              <p>Once the Parent Account holder has approved or declined the request, the record is retained for 90 days and is then deleted automatically, including any explanation written by the child.</p>
              <p>The approval itself is stored separately as a setting and is not affected by this deletion.</p>
              <p>A request that has not yet been decided is not subject to this period and is retained until the Parent Account holder resolves it.</p>
              <h3>16.5 Activity Information</h3>
              <p>Activity-related information generated through Active Boost may be retained for purposes including:</p>
              <ul>
                <li>achievement tracking;</li>
                <li>reward calculations;</li>
                <li>analytics;</li>
                <li>historical reporting; and</li>
                <li>App functionality.</li>
              </ul>
              <h3>16.6 Backup Copies</h3>
              <p>Information may continue to exist within backup systems for a limited period following deletion requests.</p>
              <p>Backup information is maintained solely for operational continuity, disaster recovery, security, and system-restoration purposes.</p>
              <h3>16.7 Anonymized and De-Identified Information</h3>
              <p>The Company may retain aggregated, anonymized, statistical, and de-identified information indefinitely provided that such information does not reasonably identify an individual.</p>
              <h3>16.8 Legal Retention Requirements</h3>
              <p>Certain information may be retained longer where required or permitted by:</p>
              <ul>
                <li>applicable law;</li>
                <li>court order;</li>
                <li>regulatory requirement;</li>
                <li>legal proceedings;</li>
                <li>investigations; or</li>
                <li>legitimate legal interests.</li>
              </ul>
              <h2>SECURITY SAFEGUARDS</h2>
              <h3>17.1 Commitment to Security</h3>
              <p>The Company employs administrative, organizational, contractual, physical, and technical safeguards designed to protect information against:</p>
              <ul>
                <li>unauthorized access;</li>
                <li>unauthorized disclosure;</li>
                <li>unauthorized use;</li>
                <li>unauthorized modification;</li>
                <li>unauthorized destruction; and</li>
                <li>accidental loss.</li>
              </ul>
              <h3>17.2 Security Measures</h3>
              <p>Security measures may include:</p>
              <ul>
                <li>encryption of data in transit;</li>
                <li>encryption of data at rest where appropriate;</li>
                <li>cryptographic hashing of authentication credentials;</li>
                <li>access controls;</li>
                <li>role-based access restrictions;</li>
                <li>authentication safeguards;</li>
                <li>monitoring and logging systems;</li>
                <li>network-security controls;</li>
                <li>backup systems; and</li>
                <li>incident-response procedures.</li>
              </ul>
              <h3>17.3 Employee and Contractor Access</h3>
              <p>Access to information is restricted to personnel, contractors, consultants, and Service Providers who require access for authorized operational purposes.</p>
              <p>Access rights are limited based on business necessity.</p>
              <h3>17.4 User Responsibilities</h3>
              <p>Users are responsible for:</p>
              <ul>
                <li>protecting account credentials;</li>
                <li>maintaining device security;</li>
                <li>safeguarding passwords and PINs;</li>
                <li>maintaining current software updates;</li>
                <li>reviewing account activity; and</li>
                <li>promptly reporting suspected unauthorized access.</li>
              </ul>
              <h3>17.5 No Absolute Security Guarantee</h3>
              <p>While the Company employs safeguards designed to protect information, no system can be guaranteed to be completely secure.</p>
              <p>Accordingly, the Company cannot guarantee that:</p>
              <ul>
                <li>unauthorized access will never occur;</li>
                <li>cybersecurity incidents will never occur;</li>
                <li>information will never be intercepted;</li>
                <li>information will never be altered; or</li>
                <li>information will never be disclosed improperly.</li>
              </ul>
              <h3>17.6 Security Incident Response</h3>
              <p>Where the Company becomes aware of a security incident affecting personal information, the Company may take actions including:</p>
              <ul>
                <li>investigating the incident;</li>
                <li>containing the incident;</li>
                <li>mitigating harm;</li>
                <li>notifying affected users where required by law; and</li>
                <li>cooperating with applicable authorities.</li>
              </ul>
              <h2>PARENT RIGHTS</h2>
              <h3>18.1 Parent Control</h3>
              <p>Parent Account holders maintain primary control over Child Profiles created within the App.</p>
              <p>Subject to applicable law and identity-verification procedures, Parent Account holders may exercise rights concerning information associated with their Child Profiles.</p>
              <h3>18.2 Access Rights</h3>
              <p>Parents may request access to information associated with:</p>
              <ul>
                <li>Child Profiles;</li>
                <li>rewards;</li>
                <li>allowances;</li>
                <li>activity information;</li>
                <li>monitoring information;</li>
                <li>analytics; and</li>
                <li>account information.</li>
              </ul>
              <h3>18.3 Correction Rights</h3>
              <p>Parents may request correction of information that is inaccurate, incomplete, outdated, or misleading.</p>
              <p>The Company may require reasonable verification before making corrections.</p>
              <h3>18.4 Deletion Rights</h3>
              <p>Parents may request deletion of Child Profile information.</p>
              <p>Deletion requests may result in:</p>
              <ul>
                <li>loss of functionality;</li>
                <li>removal of historical information;</li>
                <li>termination of Child Profiles; and</li>
                <li>closure of related services.</li>
              </ul>
              <p>Certain information may be retained where required by law or reasonably necessary for legitimate operational purposes.</p>
              <h3>18.5 Consent Management</h3>
              <p>Parents may:</p>
              <ul>
                <li>provide consent;</li>
                <li>withdraw consent;</li>
                <li>modify permissions;</li>
                <li>remove Child Profiles; and</li>
                <li>discontinue participation in optional features.</li>
              </ul>
              <p>Withdrawal of consent may affect the availability of App functionality.</p>
              <h3>18.6 Verification Requirements</h3>
              <p>Before responding to requests relating to a Child Profile, the Company may require reasonable verification of:</p>
              <ul>
                <li>identity;</li>
                <li>account ownership;</li>
                <li>parental authority; and</li>
                <li>legal authority to act on behalf of the child.</li>
              </ul>
              <h3>18.7 Limitations</h3>
              <p>Parent rights are subject to:</p>
              <ul>
                <li>applicable law;</li>
                <li>legal obligations;</li>
                <li>security requirements;</li>
                <li>fraud-prevention requirements;</li>
                <li>protection of third-party rights; and</li>
                <li>operational limitations.</li>
              </ul>
              <h2>CHILD PRIVACY RIGHTS</h2>
              <h3>19.1 Protection of Children’s Information</h3>
              <p>The Company recognizes that information relating to children warrants enhanced protection.</p>
              <p>The Company seeks to limit collection of child information to that reasonably necessary for operation of the App.</p>
              <h3>19.2 Parent Exercise of Rights</h3>
              <p>Because Child Profiles are administered through Parent Accounts, privacy rights relating to Child Profiles are generally exercised by the Parent Account holder.</p>
              <h3>19.3 Review of Child Information</h3>
              <p>Subject to verification procedures, Parent Account holders may request review of information associated with a Child Profile.</p>
              <h3>19.4 Correction of Child Information</h3>
              <p>Parents may request correction of information associated with a Child Profile where such information is inaccurate or incomplete.</p>
              <h3>19.5 Deletion of Child Information</h3>
              <p>Parents may request deletion of information associated with a Child Profile.</p>
              <p>Following a verified deletion request, the Company may:</p>
              <ul>
                <li>delete information;</li>
                <li>anonymize information;</li>
                <li>de-identify information; or</li>
                <li>restrict processing of information,</li>
              </ul>
              <p>subject to applicable legal and operational requirements.</p>
              <h3>19.6 Limitation on Direct Child Requests</h3>
              <p>The Company may require requests concerning Child Profiles to be submitted through the Parent Account holder or another individual possessing legal authority to act on behalf of the child.</p>
              <h3>19.7 Commitment to Child Privacy</h3>
              <p>The Company does not knowingly:</p>
              <ul>
                <li>sell Child Profile information;</li>
                <li>use Child Profile information for behavioural advertising;</li>
                <li>create advertising profiles relating to children; or</li>
                <li>knowingly permit independent child registration.</li>
              </ul>
              <h2>ACCESS, CORRECTION, PORTABILITY, DELETION, AND PRIVACY REQUESTS</h2>
              <h3>20.1 Privacy Requests</h3>
              <p>Users may submit requests relating to:</p>
              <ul>
                <li>access;</li>
                <li>correction;</li>
                <li>deletion;</li>
                <li>portability;</li>
                <li>consent withdrawal;</li>
                <li>privacy complaints; and</li>
                <li>information-management practices.</li>
              </ul>
              <p>Requests may be submitted using the contact information provided in this Privacy Policy.</p>
              <h3>20.2 Access Requests</h3>
              <p>Subject to applicable law, users may request access to personal information under the Company’s control.</p>
              <p>The Company may require identity verification before responding.</p>
              <h3>20.3 Correction Requests</h3>
              <p>Users may request correction of information that is inaccurate, incomplete, or outdated.</p>
              <p>Where appropriate, the Company may update records following verification.</p>
              <h3>20.4 Portability Requests</h3>
              <p>Where applicable law provides portability rights, users may request a copy of certain information in a structured and commonly used format.</p>
              <p>The Company may impose reasonable verification requirements before fulfilling such requests.</p>
              <h3>20.5 Deletion Requests</h3>
              <p>Users may request deletion of personal information.</p>
              <p>Deletion may result in:</p>
              <ul>
                <li>account closure;</li>
                <li>loss of historical information;</li>
                <li>loss of rewards information;</li>
                <li>loss of analytics information; and</li>
                <li>termination of services.</li>
              </ul>
              <p>Certain information may continue to be retained where required by law or reasonably necessary for legitimate business purposes.</p>
              <h3>20.6 Response Timeframes</h3>
              <p>The Company will use commercially reasonable efforts to respond to verified privacy requests within a reasonable period, subject to:</p>
              <ul>
                <li>request complexity;</li>
                <li>verification requirements;</li>
                <li>legal obligations; and</li>
                <li>operational considerations.</li>
              </ul>
              <h3>20.7 Refusal of Requests</h3>
              <p>The Company may refuse, limit, or defer a request where permitted by law, including where:</p>
              <ul>
                <li>identity cannot be verified;</li>
                <li>legal obligations prevent compliance;</li>
                <li>third-party rights may be affected;</li>
                <li>security concerns exist; or</li>
                <li>the request is manifestly excessive or abusive.</li>
              </ul>
              <h3>20.8 Privacy Complaints</h3>
              <p>Individuals who believe their privacy rights have been violated may submit a complaint to the Company’s Privacy Officer.</p>
              <p>The Company will investigate complaints in accordance with its internal privacy procedures and applicable law.</p>
              <h2>LEGAL REQUESTS, REGULATORY COMPLIANCE, AND PROTECTION OF RIGHTS</h2>
              <h3>21.1 Compliance with Law</h3>
              <p>The Company may collect, use, retain, disclose, preserve, or otherwise process information where reasonably necessary to:</p>
              <ul>
                <li>comply with applicable law;</li>
                <li>comply with legal obligations;</li>
                <li>satisfy regulatory requirements;</li>
                <li>respond to lawful requests;</li>
                <li>enforce legal rights; or</li>
                <li>protect users, children, and the public.</li>
              </ul>
              <h3>21.2 Legal Requests</h3>
              <p>The Company may disclose information in response to:</p>
              <ul>
                <li>court orders;</li>
                <li>subpoenas;</li>
                <li>warrants;</li>
                <li>production orders;</li>
                <li>regulatory demands;</li>
                <li>governmental requests;</li>
                <li>law-enforcement requests; or</li>
                <li>other legal processes.</li>
              </ul>
              <p>The Company may, where legally permitted, notify affected users before responding to a request.</p>
              <h3>21.3 Preservation of Information</h3>
              <p>The Company may preserve information where reasonably necessary to:</p>
              <ul>
                <li>comply with legal obligations;</li>
                <li>investigate suspected misconduct;</li>
                <li>preserve evidence;</li>
                <li>respond to legal proceedings;</li>
                <li>enforce agreements; or</li>
                <li>protect legal rights.</li>
              </ul>
              <h3>21.4 Regulatory Cooperation</h3>
              <p>The Company may cooperate with:</p>
              <ul>
                <li>privacy regulators;</li>
                <li>consumer-protection authorities;</li>
                <li>child-protection authorities;</li>
                <li>law-enforcement agencies;</li>
                <li>courts; and</li>
                <li>governmental entities</li>
              </ul>
              <p>where such cooperation is required or permitted by law.</p>
              <h3>21.5 Protection of Rights and Safety</h3>
              <p>The Company may disclose information where reasonably necessary to:</p>
              <ul>
                <li>protect the safety of a child;</li>
                <li>protect users;</li>
                <li>protect Company personnel;</li>
                <li>prevent fraud;</li>
                <li>investigate abuse of the App;</li>
                <li>protect intellectual property;</li>
                <li>enforce contractual rights; or</li>
                <li>prevent unlawful conduct.</li>
              </ul>
              <h3>21.6 Emergency Circumstances</h3>
              <p>Where the Company reasonably believes that disclosure is necessary to prevent imminent harm to an individual or the public, the Company may disclose information to appropriate authorities to the extent permitted by law.</p>
              <h2>BUSINESS TRANSACTIONS AND CORPORATE CHANGES</h2>
              <h3>22.1 Corporate Transactions</h3>
              <p>Information may be transferred, disclosed, reviewed, or otherwise processed in connection with:</p>
              <ul>
                <li>mergers;</li>
                <li>acquisitions;</li>
                <li>financings;</li>
                <li>investments;</li>
                <li>restructurings;</li>
                <li>reorganizations;</li>
                <li>asset sales;</li>
                <li>insolvency proceedings; or</li>
                <li>similar corporate transactions.</li>
              </ul>
              <h3>22.2 Due Diligence Activities</h3>
              <p>During a contemplated transaction, prospective investors, lenders, purchasers, advisors, auditors, accountants, consultants, and legal counsel may review certain information as part of due-diligence activities.</p>
              <p>Where reasonably appropriate, such parties may be subject to confidentiality obligations.</p>
              <h3>22.3 Continuity of Services</h3>
              <p>Information may be transferred as part of a transaction to ensure continuity of services and operation of the App.</p>
              <h3>22.4 Notice of Material Changes</h3>
              <p>Where required by applicable law, users may be provided notice of a transaction affecting the handling of personal information.</p>
              <h3>22.5 Successor Organizations</h3>
              <p>Any successor organization receiving information may continue to process such information in accordance with:</p>
              <ul>
                <li>this Privacy Policy;</li>
                <li>successor privacy policies;</li>
                <li>applicable law; and</li>
                <li>contractual obligations applicable to the transaction.</li>
              </ul>
              <h2>COOKIES, LOCAL STORAGE, AND SIMILAR TECHNOLOGIES</h2>
              <h3>23.1 Use of Technical Technologies</h3>
              <p>The App and related services may utilize technologies including:</p>
              <ul>
                <li>cookies;</li>
                <li>local storage;</li>
                <li>session tokens;</li>
                <li>authentication tokens;</li>
                <li>software development kits (SDKs);</li>
                <li>device identifiers;</li>
                <li>application storage mechanisms; and</li>
                <li>similar technologies.</li>
              </ul>
              <h3>23.2 Purposes</h3>
              <p>Such technologies may be used to:</p>
              <ul>
                <li>authenticate users;</li>
                <li>maintain sessions;</li>
                <li>preserve settings;</li>
                <li>maintain security;</li>
                <li>operate functionality;</li>
                <li>improve reliability;</li>
                <li>support notifications;</li>
                <li>diagnose technical issues; and</li>
                <li>improve user experience.</li>
              </ul>
              <h3>23.3 Essential Technologies</h3>
              <p>Certain technologies are essential to operation of the App and cannot reasonably be disabled without impairing functionality.</p>
              <h3>23.4 Third-Party Technologies</h3>
              <p>Third-party Service Providers may utilize technical technologies necessary to provide services to the Company.</p>
              <p>Such providers may operate under their own privacy practices and contractual obligations.</p>
              <h3>23.5 Browser Controls</h3>
              <p>Where applicable, users may control certain technologies through browser, device, or operating-system settings.</p>
              <p>Disabling certain technologies may affect App functionality.</p>
              <h2>MARKETING COMMUNICATIONS AND COMMERCIAL ELECTRONIC MESSAGES</h2>
              <h3>24.1 Service Communications</h3>
              <p>The Company may send communications relating to:</p>
              <ul>
                <li>account administration;</li>
                <li>security;</li>
                <li>support;</li>
                <li>legal notices;</li>
                <li>subscription information;</li>
                <li>service updates; and</li>
                <li>operational matters.</li>
              </ul>
              <p>Such communications are considered service-related communications and may be sent regardless of marketing preferences where permitted by law.</p>
              <h3>24.2 Marketing Communications</h3>
              <p>The Company may send marketing communications concerning:</p>
              <ul>
                <li>new features;</li>
                <li>promotions;</li>
                <li>premium services;</li>
                <li>rewards programs;</li>
                <li>educational content; and</li>
                <li>other offerings.</li>
              </ul>
              <p>Marketing communications will be provided in accordance with applicable law.</p>
              <h3>24.3 Consent</h3>
              <p>Where required by law, marketing communications will be sent only with appropriate consent.</p>
              <h3>24.4 Withdrawal of Consent</h3>
              <p>Users may withdraw consent to receive marketing communications at any time by:</p>
              <ul>
                <li>following unsubscribe instructions;</li>
                <li>adjusting account settings; or</li>
                <li>contacting the Company.</li>
              </ul>
              <p>Withdrawal of consent does not affect service-related communications.</p>
              <h3>24.5 Children’s Information</h3>
              <p>The Company does not knowingly use information associated with Child Profiles for direct marketing purposes.</p>
              <p>The Company does not knowingly direct marketing communications to children through Child Profiles.</p>
              <h3>24.6 Compliance with Anti-Spam Laws</h3>
              <p>The Company intends to comply with applicable commercial electronic messaging and anti-spam legislation, including Canada’s Anti-Spam Legislation (CASL).</p>
              <h2>GOOGLE PLAY FAMILIES, APPLE APP STORE, AND CHILD PRIVACY COMPLIANCE</h2>
              <h3>25.1 Commitment to Child Privacy</h3>
              <p>The Company recognizes that applications directed toward families and children require enhanced privacy protections.</p>
              <p>The Company seeks to design the App in a manner consistent with applicable child privacy principles and marketplace requirements.</p>
              <h3>25.2 Google Play Families Compliance</h3>
              <p>Where applicable, the Company seeks to operate the App in a manner consistent with:</p>
              <ul>
                <li>Google Play Families policies;</li>
                <li>Google Play Developer Program Policies;</li>
                <li>Google Play Data Safety requirements; and</li>
                <li>related marketplace requirements.</li>
              </ul>
              <h3>25.3 Apple App Store Compliance</h3>
              <p>Where applicable, the Company seeks to operate the App in a manner consistent with:</p>
              <ul>
                <li>Apple App Store Review Guidelines;</li>
                <li>App Store privacy requirements;</li>
                <li>App Tracking Transparency requirements; and</li>
                <li>related Apple marketplace policies.</li>
              </ul>
              <h3>25.4 Child Data Protections</h3>
              <p>The Company does not knowingly:</p>
              <ul>
                <li>sell Child Profile information;</li>
                <li>engage in behavioural advertising directed at children;</li>
                <li>use Child Profile information for cross-context advertising;</li>
                <li>create advertising profiles relating to children; or</li>
                <li>knowingly permit independent child registration.</li>
              </ul>
              <h3>25.5 Parent-Controlled Environment</h3>
              <p>The App is designed so that Child Profiles are created, managed, supervised, and controlled by Parent Account holders.</p>
              <h3>25.6 Data Minimization</h3>
              <p>The Company seeks to limit collection of information relating to children to information reasonably necessary for operation of the App.</p>
              <h3>25.7 Accessibility Service Disclosure</h3>
              <p>Certain App functionality may rely upon Android Accessibility Service permissions and related permissions.</p>
              <p>Such permissions are used solely to support authorized functionality described in this Privacy Policy and the Terms of Service.</p>
              <p>The Company does not use Accessibility Services to collect information beyond what is reasonably necessary to provide authorized App functionality.</p>
              <h3>25.8 Usage Access Disclosure</h3>
              <p>Certain App functionality may rely upon Android Usage Access permissions.</p>
              <p>Usage Access information is used solely for operation of monitoring and reporting functionality authorized by the Parent Account holder.</p>
              <h3>25.9 Activity Recognition Disclosure</h3>
              <p>Where Active Boost is enabled, the App may utilize Activity Recognition permissions to obtain activity-related information for reward and incentive functionality.</p>
              <h3>25.10 Future Compliance Measures</h3>
              <p>The Company reserves the right to modify App functionality, permissions, privacy practices, consent mechanisms, and compliance measures in order to comply with:</p>
              <ul>
                <li>legal requirements;</li>
                <li>regulatory requirements;</li>
                <li>marketplace requirements; and</li>
                <li>evolving privacy standards.</li>
              </ul>
              <h2>CHANGES TO THIS PRIVACY POLICY</h2>
              <h3>26.1 Right to Modify</h3>
              <p>The Company reserves the right to amend, revise, supplement, replace, or otherwise modify this Privacy Policy at any time.</p>
              <p>Changes may be made to reflect:</p>
              <ul>
                <li>changes in applicable law;</li>
                <li>changes in regulatory requirements;</li>
                <li>changes in marketplace requirements;</li>
                <li>changes in App functionality;</li>
                <li>changes in Service Providers;</li>
                <li>operational changes;</li>
                <li>security requirements; or</li>
                <li>business developments.</li>
              </ul>
              <h3>26.2 Notification of Changes</h3>
              <p>Where the Company determines that a change is material, the Company may provide notice through one or more of the following methods:</p>
              <ul>
                <li>in-App notifications;</li>
                <li>email communications;</li>
                <li>website notices;</li>
                <li>account notifications; or</li>
                <li>other reasonable means.</li>
              </ul>
              <h3>26.3 Effective Date of Changes</h3>
              <p>Unless otherwise stated, amendments to this Privacy Policy become effective on the date identified in the revised Privacy Policy.</p>
              <h3>26.4 Continued Use</h3>
              <p>Subject to applicable law, continued use of the App following implementation of a revised Privacy Policy constitutes acknowledgement of the revised Privacy Policy.</p>
              <h3>26.5 Prior Versions</h3>
              <p>The Company may retain prior versions of this Privacy Policy for legal, regulatory, operational, and historical purposes.</p>
              <h2>PRIVACY OFFICER AND CONTACT INFORMATION</h2>
              <h3>27.1 Privacy Officer</h3>
              <p>The Company has designated a Privacy Officer responsible for oversight of privacy matters, privacy compliance, privacy requests, and privacy complaints.</p>
              <h3>27.2 Privacy Inquiries</h3>
              <p>Questions regarding this Privacy Policy or the Company’s privacy practices may be directed to:</p>
              <p>Privacy Officer</p>
              <p>2422019 Ontario Inc. (o/a BuckTime!)</p>
              <p>Email: privacy@chachingapp.ca</p>
              <h3>27.3 General Support</h3>
              <p>General support inquiries may be directed to:</p>
              <p>Email: support@chachingapp.ca</p>
              <h3>27.4 Legal Inquiries</h3>
              <p>Legal notices, regulatory communications, intellectual property complaints, and other legal inquiries may be directed to:</p>
              <p>Email: legal@chachingapp.ca</p>
              <h3>27.5 Future Contact Information</h3>
              <p>The Company reserves the right to update contact information from time to time.</p>
              <p>Current contact information may be made available through:</p>
              <ul>
                <li>the App;</li>
                <li>the Company’s website; or</li>
                <li>updated versions of this Privacy Policy.</li>
              </ul>
              <h2>PRIVACY COMPLAINTS AND DISPUTE RESOLUTION</h2>
              <h3>28.1 Privacy Complaints</h3>
              <p>Individuals who believe that the Company has failed to comply with this Privacy Policy or applicable privacy legislation may submit a privacy complaint to the Privacy Officer.</p>
              <h3>28.2 Complaint Information</h3>
              <p>Privacy complaints should include:</p>
              <ul>
                <li>the complainant’s name;</li>
                <li>contact information;</li>
                <li>a description of the concern;</li>
                <li>relevant dates;</li>
                <li>supporting information; and</li>
                <li>the relief sought.</li>
              </ul>
              <h3>28.3 Investigation Process</h3>
              <p>The Company will use commercially reasonable efforts to:</p>
              <ul>
                <li>review complaints;</li>
                <li>investigate concerns;</li>
                <li>obtain relevant information;</li>
                <li>assess compliance obligations; and</li>
                <li>respond to the complainant.</li>
              </ul>
              <h3>28.4 Response Time</h3>
              <p>Response times may vary depending upon:</p>
              <ul>
                <li>the complexity of the complaint;</li>
                <li>verification requirements;</li>
                <li>legal obligations;</li>
                <li>availability of information; and</li>
                <li>operational considerations.</li>
              </ul>
              <h3>28.5 Regulatory Rights</h3>
              <p>Nothing in this Privacy Policy restricts an individual’s right to submit a complaint to:</p>
              <ul>
                <li>applicable privacy regulators;</li>
                <li>consumer-protection authorities;</li>
                <li>governmental agencies; or</li>
                <li>other authorities having jurisdiction.</li>
              </ul>
              <h3>28.6 Alternative Resolution</h3>
              <p>The Company may, but is not obligated to, attempt to resolve privacy concerns through informal discussions, mediation, or other voluntary dispute-resolution processes.</p>
              <h2>INTERPRETATION, SEVERABILITY, AND GOVERNING PRIVACY PRINCIPLES</h2>
              <h3>29.1 Interpretation</h3>
              <p>This Privacy Policy shall be interpreted in a manner consistent with:</p>
              <ul>
                <li>applicable privacy legislation;</li>
                <li>consumer-protection legislation;</li>
                <li>child privacy principles;</li>
                <li>marketplace requirements; and</li>
                <li>applicable legal obligations.</li>
              </ul>
              <h3>29.2 Severability</h3>
              <p>If any provision of this Privacy Policy is determined to be:</p>
              <ul>
                <li>invalid;</li>
                <li>illegal;</li>
                <li>unenforceable; or</li>
                <li>contrary to applicable law,</li>
              </ul>
              <p>that provision shall be interpreted, modified, limited, or severed to the minimum extent necessary to preserve validity and enforceability.</p>
              <p>The remainder of this Privacy Policy shall remain in full force and effect.</p>
              <h3>29.3 No Waiver</h3>
              <p>Failure by the Company to enforce any provision of this Privacy Policy shall not constitute:</p>
              <ul>
                <li>a waiver;</li>
                <li>an election;</li>
                <li>an estoppel; or</li>
                <li>a relinquishment of rights.</li>
              </ul>
              <h3>29.4 Consistency with Applicable Law</h3>
              <p>Where applicable law grants privacy rights that cannot be waived, restricted, or excluded, such rights shall prevail to the extent required by law.</p>
              <h3>29.5 Privacy Principles</h3>
              <p>The Company seeks to administer this Privacy Policy in accordance with principles including:</p>
              <ul>
                <li>accountability;</li>
                <li>transparency;</li>
                <li>consent;</li>
                <li>data minimization;</li>
                <li>security safeguards;</li>
                <li>limited use and disclosure;</li>
                <li>openness;</li>
                <li>individual access; and</li>
                <li>responsible information management.</li>
              </ul>
              <h2>EFFECTIVE DATE</h2>
              <h3>30.1 Effective Date</h3>
              <p>This Privacy Policy is effective as of: March 6, 2026</p>
              <h3>30.2 Last Updated</h3>
              <h3>30.3 Acknowledgement</h3>
              <p>By creating a Parent Account, establishing a Child Profile, accessing the App, or otherwise using the App, users acknowledge that they have reviewed and understood this Privacy Policy.</p>
              <h3>30.4 Official Version</h3>
              <p>This Privacy Policy constitutes the official privacy policy governing the collection, use, disclosure, storage, retention, transfer, and protection of information associated with the App.</p>
              <h3>30.5 Survival</h3>
              <p>The provisions of this Privacy Policy that by their nature are intended to survive account deletion, service termination, or discontinuance of the App shall survive to the extent reasonably necessary to:</p>
              <ul>
                <li>comply with legal obligations;</li>
                <li>resolve disputes;</li>
                <li>protect rights;</li>
                <li>maintain records;</li>
                <li>enforce agreements; and</li>
                <li>complete lawful operational activities.</li>
              </ul>
              <p>SCHEDULE &quot;A&quot;</p>
              <h2>CURRENT SERVICE PROVIDERS AND INFRASTRUCTURE PROVIDERS</h2>
              <p>This Schedule forms part of the BuckTime! Privacy Policy.</p>
              <p>The Company may update this Schedule from time to time without amending the substantive provisions of the Privacy Policy, provided that any changes remain consistent with the Privacy Policy.</p>
              <p>The providers identified below assist the Company in operating, maintaining, securing, and supporting the App.</p>
              <p>A.1 Google Sign-In</p>
              <p>Provider: Google LLC</p>
              <p>Purpose:</p>
              <p>Authentication and account sign-in services.</p>
              <p>Information Potentially Processed:</p>
              <ul>
                <li>name;</li>
                <li>email address;</li>
                <li>Google account identifier; and</li>
                <li>profile image reference information.</li>
              </ul>
              <p>Purpose of Processing:</p>
              <ul>
                <li>account creation;</li>
                <li>account authentication;</li>
                <li>account security;</li>
                <li>account recovery; and</li>
                <li>fraud prevention.</li>
              </ul>
              <p>Users who elect to authenticate through Google Sign-In remain subject to Google’s privacy practices and terms of service.</p>
              <p>A.2 Firebase Cloud Messaging (FCM)</p>
              <p>Provider: Google LLC</p>
              <p>Purpose:</p>
              <p>Push-notification delivery.</p>
              <p>Information Potentially Processed:</p>
              <ul>
                <li>device tokens;</li>
                <li>notification identifiers;</li>
                <li>account identifiers;</li>
                <li>notification preferences; and</li>
                <li>related technical information.</li>
              </ul>
              <p>Purpose of Processing:</p>
              <ul>
                <li>Focus Window notifications;</li>
                <li>reward notifications;</li>
                <li>security notifications;</li>
                <li>administrative communications; and</li>
                <li>service announcements.</li>
              </ul>
              <p>The Company does not control Google’s independent processing activities associated with Firebase Cloud Messaging.</p>
              <p>A.3 Expo Push Services</p>
              <p>Provider: Expo</p>
              <p>Purpose:</p>
              <p>Push-notification infrastructure and delivery services.</p>
              <p>Information Potentially Processed:</p>
              <ul>
                <li>push-notification identifiers;</li>
                <li>device identifiers;</li>
                <li>notification payload information; and</li>
                <li>related technical information.</li>
              </ul>
              <p>Purpose of Processing:</p>
              <ul>
                <li>delivery of App notifications;</li>
                <li>service communications;</li>
                <li>account notifications; and</li>
                <li>operational messaging.</li>
              </ul>
              <p>A.4 MongoDB Atlas</p>
              <p>Provider: MongoDB, Inc.</p>
              <p>Purpose:</p>
              <p>Database hosting and storage services.</p>
              <p>Information Potentially Processed:</p>
              <ul>
                <li>Parent Account information;</li>
                <li>Child Profile information;</li>
                <li>Family Group information;</li>
                <li>rewards information;</li>
                <li>activity information;</li>
                <li>monitoring information;</li>
                <li>analytics information;</li>
                <li>account settings; and</li>
                <li>operational data.</li>
              </ul>
              <p>Primary Data Region:</p>
              <p>Canada (subject to provider infrastructure, backup systems, support access, and future service changes).</p>
              <p>Purpose of Processing:</p>
              <ul>
                <li>database storage;</li>
                <li>application operations;</li>
                <li>backup and recovery;</li>
                <li>security monitoring; and</li>
                <li>system administration.</li>
              </ul>
              <p>A.5 Fly.io</p>
              <p>Provider: Fly.io</p>
              <p>Purpose:</p>
              <p>Application hosting and infrastructure services.</p>
              <p>Information Potentially Processed:</p>
              <ul>
                <li>technical information;</li>
                <li>application logs;</li>
                <li>diagnostic information;</li>
                <li>account identifiers;</li>
                <li>security information; and</li>
                <li>operational data.</li>
              </ul>
              <p>Purpose of Processing:</p>
              <ul>
                <li>application hosting;</li>
                <li>service delivery;</li>
                <li>infrastructure management;</li>
                <li>system reliability;</li>
                <li>performance monitoring; and</li>
                <li>operational support.</li>
              </ul>
              <p>A.6 Future Service Providers</p>
              <p>The Company may introduce additional Service Providers from time to time, including:</p>
              <ul>
                <li>payment processors;</li>
                <li>subscription-management providers;</li>
                <li>customer-support platforms;</li>
                <li>analytics providers;</li>
                <li>cybersecurity providers;</li>
                <li>fraud-prevention providers;</li>
                <li>communications providers;</li>
                <li>cloud-hosting providers;</li>
                <li>artificial-intelligence service providers; and</li>
                <li>other operational vendors.</li>
              </ul>
              <p>Where required by applicable law, the Company will update this Schedule, the Privacy Policy, or related disclosures to reflect material changes.</p>
              <p>A.7 Service Provider Selection</p>
              <p>The Company seeks to engage reputable Service Providers that implement security, confidentiality, and privacy safeguards appropriate to the nature of the services provided.</p>
              <p>However, each Service Provider operates independently and remains responsible for its own privacy, security, and operational practices.</p>
              <p>Users should review the privacy policies of applicable Service Providers for additional information.</p>
              <p>A.8 Changes to this Schedule</p>
              <p>The Company reserves the right to:</p>
              <ul>
                <li>add Service Providers;</li>
                <li>remove Service Providers;</li>
                <li>replace Service Providers;</li>
                <li>change hosting providers;</li>
                <li>change infrastructure providers;</li>
                <li>change notification providers; and</li>
                <li>modify technical architecture</li>
              </ul>
              <p>at any time.</p>
              <p>Updated versions of this Schedule may be published through:</p>
              <ul>
                <li>the App;</li>
                <li>the Company’s website; or</li>
                <li>updated versions of the Privacy Policy.</li>
              </ul>
              <p>The most current version of this Schedule shall govern.</p>

          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default PrivacyPage;
